import buildQuery from "odata-query"
import { HTTPQuery, type HTTPQueryValue } from "@/builder/http-query"

export type ODataValue =
  | string
  | number
  | boolean
  | Date
  | { type: "guid" | "duration" | "binary" | "decimal" | "raw"; value: string }

type Dir = "asc" | "desc"

type QueryObj = {
  filter?: unknown
  select?: string[]
  orderBy?: string | string[]
  expand?: Record<string, any> | string | Array<string | object>
  top?: number
  skip?: number
  search?: string
  count?: boolean | object
  key?: number | string | object
  action?: string | object
  func?: string | object
  transform?: object | object[]
}

/**
 * Small, safe-by-default OData builder.
 * F is a union of field names for autocomplete, e.g. type F = "status" | "date" | ...
 */
export class ODataQueryBuilder<F extends string = string> {
  /** Tag -> fragment */
  private parts = new Map<string, unknown>()

  private _select: string[] = []
  private _orderBy: string[] = []
  private _expand: Record<string, any> | undefined
  private _top?: number
  private _skip?: number
  private _search?: string
  private _count?: boolean | object
  private _key?: number | string | object
  private _action?: string | object
  private _func?: string | object
  private _transform?: object | object[]

  /**
   * Non-OData query-string extras folded into the final {@link HTTPQuery}.
   * Stored in a {@link Map}, so repeated uses of the same key are last-write-wins
   * and earlier values are overwritten rather than preserved as duplicates.
   */
  private readonly _extras = new Map<string, HTTPQueryValue>()

  // ---------- Comparators ----------
  eq(field: F, value: ODataValue | undefined | null, tag = `eq:${field}`) {
    if (value === undefined) return this
    return this.put(tag, { [field]: value })
  }
  ne(field: F, value: ODataValue | undefined | null, tag = `ne:${field}`) {
    if (value === undefined) return this
    return this.put(tag, { [field]: { ne: value } })
  }
  gt(field: F, value: ODataValue | undefined | null, tag = `gt:${field}`) {
    if (value === undefined || value === null) return this
    return this.put(tag, { [field]: { gt: value } })
  }
  ge(field: F, value: ODataValue | undefined | null, tag = `ge:${field}`) {
    if (value === undefined || value === null) return this
    return this.put(tag, { [field]: { ge: value } })
  }
  lt(field: F, value: ODataValue | undefined | null, tag = `lt:${field}`) {
    if (value === undefined || value === null) return this
    return this.put(tag, { [field]: { lt: value } })
  }
  le(field: F, value: ODataValue | undefined | null, tag = `le:${field}`) {
    if (value === undefined || value === null) return this
    return this.put(tag, { [field]: { le: value } })
  }
  in(field: F, values: ODataValue[] | undefined | null, tag = `in:${field}`) {
    if (!values || values.length === 0) return this
    return this.put(tag, { [field]: { in: values } })
  }
  /** Adds ge/le as available; gracefully handles missing ends */
  between(field: F, start?: ODataValue | null, end?: ODataValue | null, tag = `between:${field}`) {
    const frag: any = {}
    if (start !== undefined && start !== null) frag.ge = start
    if (end !== undefined && end !== null) frag.le = end
    if (Object.keys(frag).length === 0) return this
    return this.put(tag, { [field]: frag })
  }

  // ---------- String predicates ----------
  contains(field: F, term?: string | null, tag = `contains:${field}`) {
    if (!term) return this
    return this.put(tag, { [field]: { contains: term } })
  }
  startswith(field: F, term?: string | null, tag = `startswith:${field}`) {
    if (!term) return this
    return this.put(tag, { [field]: { startswith: term } })
  }
  endswith(field: F, term?: string | null, tag = `endswith:${field}`) {
    if (!term) return this
    return this.put(tag, { [field]: { endswith: term } })
  }

  /**
   * Raw odata-query fragment, for shapes the comparators above can't express.
   *
   * Mainly `any()` lambdas over a child collection, e.g.
   * `{ instructions: { any: { date: { ge: from, le: to } } } }`, which compiles to
   * `instructions/any(...)` -> `Exists()` on the server, instead of a relation
   * traversal (`instructions__date`) which produces a JOIN and duplicate parent rows.
   *
   * Keep every condition for one child row inside a SINGLE `any` object. Splitting them
   * across two `any` calls lets two different children each satisfy one half.
   */
  raw(tag: string, fragment: unknown) {
    return this.put(tag, fragment)
  }

  // ---------- Groups ----------
  /** OR of a nested sub-builder */
  or(tag: string, build: (b: ODataQueryBuilder<F>) => void) {
    const b = new ODataQueryBuilder<F>()
    build(b)
    const arr = b.fragments()
    if (arr.length === 0) return this
    return this.put(tag, { or: arr })
  }
  and(tag: string, build: (b: ODataQueryBuilder<F>) => void) {
    const b = new ODataQueryBuilder<F>()
    build(b)
    const arr = b.fragments()
    if (arr.length === 0) return this
    return this.put(tag, { and: arr })
  }
  /** NOT of a nested AND group */
  not(tag: string, build: (b: ODataQueryBuilder<F>) => void) {
    const b = new ODataQueryBuilder<F>()
    build(b)
    const arr = b.fragments()
    if (arr.length === 0) return this
    return this.put(tag, { not: { and: arr } })
  }

  // ---------- Query-level ----------
  select(...fields: string[]) {
    if (fields.length) this._select.push(...fields)
    return this
  }
  orderBy(field: string, dir: Dir = "asc") {
    this._orderBy.push(dir ? `${field} ${dir}` : field)
    return this
  }
  top(n: number) {
    this._top = n
    return this
  }
  skip(n: number) {
    this._skip = n
    return this
  }
  search(expr?: string | null) {
    if (expr) this._search = expr
    return this
  }
  count(v: boolean | object = true) {
    this._count = v
    return this
  }
  key(v: number | string | object) {
    this._key = v
    return this
  }
  action(v: string | object) {
    this._action = v
    return this
  }
  func(v: string | object) {
    this._func = v
    return this
  }
  transform(v: object | object[]) {
    this._transform = v
    return this
  }

  /**
   * Add a non-OData query-string parameter (e.g. backend-specific flags like
   * `deep`, `normalize`, `getNested`). Folded into the final {@link HTTPQuery}
   * by {@link build}. Passing `undefined`/`null` is a no-op.
   */
  with(key: string, value: HTTPQueryValue | undefined | null) {
    if (value === undefined || value === null) return this
    this._extras.set(key, value)
    return this
  }

  /** Remove a previously-set extra. */
  unsetWith(key: string) {
    this._extras.delete(key)
    return this
  }

  /**
   * Request a fully-expanded response by appending `$expand=*` to the query string.
   *
   * Pass `false` to disable (useful for conditional chains, e.g. `.deep(isDeep)`).
   * Implemented via {@link with} so it bypasses `odata-query`'s expand-object
   * serialization, which expects field-keyed objects rather than `"*"`.
   */
  deep(enabled: boolean = true) {
    return enabled ? this.with("$expand", "*") : this.unsetWith("$expand")
  }

  /** Conditional apply */
  if(cond: any, build: (b: this) => void) {
    if (cond) build(this)
    return this
  }

  /** Remove/replace a fragment later */
  unset(tag: string) {
    this.parts.delete(tag)
    return this
  }
  set(tag: string, build: (b: this) => void) {
    this.unset(tag)
    build(this)
    return this
  }

  // ---------- Results ----------
  toObject(): QueryObj {
    const arr = this.fragments()
    const q: QueryObj = {}
    if (arr.length === 1) q.filter = arr[0]
    else if (arr.length > 1) q.filter = arr // implied AND
    if (this._select.length) q.select = [...new Set(this._select)]
    if (this._orderBy.length) q.orderBy = this._orderBy
    if (this._expand) q.expand = this._expand
    if (this._top != null) q.top = this._top
    if (this._skip != null) q.skip = this._skip
    if (this._search) q.search = this._search
    if (this._count !== undefined) q.count = this._count
    if (this._key !== undefined) q.key = this._key
    if (this._action !== undefined) q.action = this._action
    if (this._func !== undefined) q.func = this._func
    if (this._transform !== undefined) q.transform = this._transform
    return q
  }
  /**
   * Produce an {@link HTTPQuery} value object. Call `.toString()` on the
   * returned value object when you need the serialized query string.
   *
   * Any extras registered via {@link with} are folded into the result.
   */
  build(): HTTPQuery {
    let query = new HTTPQuery(buildQuery(this.toObject() as any))
    for (const [key, value] of this._extras) {
      query = query.with(key, value)
    }
    return query
  }

  // ---------- internals ----------
  private put(tag: string, fragment: unknown) {
    this.parts.set(tag, fragment)
    return this
  }
  private fragments(): any[] {
    return [...this.parts.values()]
  }
}
